<template>
  <div
    class="pcm"
    :class="{ 'pcm--chat-open': activeRecipient }"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('Components.PrivateChatModal.privateChat')"
  >
    <!-- ═══ LEFT PANEL — Conversation list ═══ -->
    <aside class="pcm__side">
      <div class="pcm__side-header">
        <h2 class="pcm__side-title">{{ $t('Views.Stage.Labels.chat') }}</h2>
        <button
          class="pcm__close pcm__close--mobile-only"
          @click="onClose"
          :aria-label="$t('Views.Stage.ParticipantsModal.close')"
          type="button"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Search -->
      <div class="pcm__search-wrap">
        <i class="fas fa-search pcm__search-icon" aria-hidden="true"></i>
        <input
          v-model="searchInput"
          type="text"
          class="pcm__search-input"
          :placeholder="$t('Components.PrivateChatModal.searchPeople')"
          :aria-label="$t('Components.PrivateChatModal.searchAttendees')"
        />
      </div>

      <!-- Recent conversations section -->
      <div v-if="recentChats.length" class="pcm__section">
        <div class="pcm__section-label">
          <i class="fas fa-caret-down"></i>
          {{ $t('Components.PrivateChatModal.recent') }}
        </div>
        <button
          v-for="chat in filteredRecent"
          :key="'chat-' + chat.ChatID"
          class="pcm__conv"
          :class="{
            'pcm__conv--active':
              activeRecipient &&
              getChatCounterpart(chat).UserID === activeRecipient.UserID
          }"
          @click="switchToChat(chat)"
          type="button"
        >
          <div class="pcm__conv-avatar-wrap">
            <Avatar
              :image="getChatCounterpart(chat).Picture"
              :circle="true"
              :name="userFullName(getChatCounterpart(chat))"
              :alt="userFullName(getChatCounterpart(chat))"
              class="pcm__conv-avatar"
            />
            <span
              v-if="isUserOnline(getChatCounterpart(chat).UserID)"
              class="pcm__conv-dot"
              :aria-label="$t('Components.PrivateChatModal.online')"
            ></span>
          </div>
          <div class="pcm__conv-body">
            <div class="pcm__conv-head">
              <span class="pcm__conv-name">
                {{ getChatCounterpart(chat).Firstname }}
                {{ getChatCounterpart(chat).Lastname }}
              </span>
              <span class="pcm__conv-time">{{
                formatChatTime(lastMessageAt(chat))
              }}</span>
            </div>
            <div class="pcm__conv-preview">{{ lastMessagePreview(chat) }}</div>
          </div>
        </button>
      </div>

      <!-- All attendees section -->
      <div class="pcm__section">
        <div class="pcm__section-label">
          <i class="fas fa-caret-down"></i>
          {{ $t('Components.PrivateChatModal.allAttendees') }}
          <span class="pcm__section-count">{{
            filteredOtherAttendees.length
          }}</span>
        </div>
        <button
          v-for="user in filteredOtherAttendees"
          :key="'user-' + user.UserID"
          class="pcm__conv pcm__conv--compact"
          :class="{
            'pcm__conv--active':
              activeRecipient && activeRecipient.UserID === user.UserID
          }"
          @click="switchToUser(user)"
          type="button"
        >
          <div class="pcm__conv-avatar-wrap">
            <Avatar
              :image="user.Picture"
              :circle="true"
              :name="userFullName(user)"
              :alt="userFullName(user)"
              class="pcm__conv-avatar"
            />
            <span
              v-if="isUserOnline(user.UserID)"
              class="pcm__conv-dot"
              :aria-label="$t('Components.PrivateChatModal.online')"
            ></span>
          </div>
          <div class="pcm__conv-body">
            <div class="pcm__conv-name">
              {{ user.Firstname }} {{ user.Lastname }}
            </div>
            <div v-if="user.Jobtitle || user.Company" class="pcm__conv-role">
              <template v-if="user.Jobtitle">{{ user.Jobtitle }}</template>
              <template v-if="user.Jobtitle && user.Company"> · </template>
              <template v-if="user.Company">{{ user.Company }}</template>
            </div>
          </div>
        </button>
        <div
          v-if="!filteredOtherAttendees.length && !recentChats.length"
          class="pcm__empty-side"
        >
          <i class="far fa-user"></i>
          <p>{{ $t('Components.PrivateChatModal.noOtherAttendees') }}</p>
        </div>
      </div>
    </aside>

    <!-- ═══ RIGHT PANEL — Chat window ═══ -->
    <section class="pcm__main">
      <!-- Header -->
      <header class="pcm__chat-header">
        <div class="pcm__user" v-if="activeRecipient">
          <button
            class="pcm__side-back"
            @click="activeRecipient = null"
            :aria-label="$t('Components.PrivateChatModal.backToList')"
            type="button"
          >
            <i class="fas fa-arrow-left"></i>
          </button>
          <div class="pcm__avatar-wrap">
            <Avatar
              :image="activeRecipient.Picture"
              :circle="true"
              :name="userFullName(activeRecipient)"
              :alt="userFullName(activeRecipient)"
              class="pcm__avatar"
            />
            <span
              v-if="isUserOnline(activeRecipient.UserID)"
              class="pcm__avatar-dot"
              :aria-label="$t('Components.PrivateChatModal.online')"
            ></span>
          </div>
          <div class="pcm__user-info">
            <div class="pcm__name">
              {{ activeRecipient.Firstname }} {{ activeRecipient.Lastname }}
            </div>
            <div
              v-if="activeRecipient.Jobtitle || activeRecipient.Company"
              class="pcm__role"
            >
              <template v-if="activeRecipient.Jobtitle">{{
                activeRecipient.Jobtitle
              }}</template>
              <template
                v-if="activeRecipient.Jobtitle && activeRecipient.Company"
              >
                ·
              </template>
              <template v-if="activeRecipient.Company">{{
                activeRecipient.Company
              }}</template>
            </div>
          </div>
        </div>
        <div v-else class="pcm__user pcm__user--placeholder">
          <div class="pcm__avatar-wrap pcm__avatar-wrap--placeholder">
            <i class="far fa-comment-dots"></i>
          </div>
          <div class="pcm__user-info">
            <div class="pcm__name">
              {{ $t('Components.PrivateChatModal.selectConversation') }}
            </div>
            <div class="pcm__role">
              {{ $t('Components.PrivateChatModal.pickSomeone') }}
            </div>
          </div>
        </div>
        <button
          class="pcm__close"
          @click="onClose"
          :aria-label="$t('Views.Stage.ParticipantsModal.close')"
        >
          <i class="fas fa-times"></i>
        </button>
      </header>

      <!-- Messages -->
      <div
        ref="messageList"
        class="pcm__messages"
        aria-live="polite"
        aria-relevant="additions"
        role="log"
      >
        <div v-if="!activeRecipient" class="pcm__empty">
          <div class="pcm__empty-icon">
            <i class="far fa-comments"></i>
          </div>
          <p class="pcm__empty-text">
            {{ $t('Components.PrivateChatModal.selectFromList') }}
          </p>
        </div>

        <template v-else>
          <div v-if="loading" class="pcm__loading">
            <i
              class="fas fa-circle-notch fa-spin"
              style="font-size: 18px; color: var(--color-primary, #047873)"
            ></i>
          </div>

          <template v-else>
            <div
              v-for="msg in messages"
              :key="msg.ChatMessageID"
              class="pcm__msg-wrap"
            >
              <div
                class="pcm__msg"
                :class="isOwnMessage(msg) ? 'pcm__msg--own' : 'pcm__msg--other'"
              >
                <Avatar
                  v-if="!isOwnMessage(msg)"
                  :image="activeRecipient.Picture"
                  :circle="true"
                  :name="userFullName(activeRecipient)"
                  :alt="userFullName(activeRecipient)"
                  class="pcm__msg-avatar"
                />
                <div class="pcm__msg-col">
                  <span v-if="!isOwnMessage(msg)" class="pcm__msg-sender">
                    {{ activeRecipient.Firstname }}
                    {{ activeRecipient.Lastname }}
                  </span>
                  <div
                    class="pcm__bubble"
                    v-html="renderMessage(msg.Message)"
                  ></div>
                  <div class="pcm__msg-time">
                    {{ formatTime(msg.CreatedAt) }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!messages.length" class="pcm__empty pcm__empty--in-chat">
              <div class="pcm__empty-icon">
                <i class="far fa-comment-dots"></i>
              </div>
              <p class="pcm__empty-text">
                {{ $t('Components.PrivateChatModal.noMessagesYet') }}<br />
                {{ $t('Components.PrivateChatModal.startConversation') }}
              </p>
            </div>
          </template>
        </template>
      </div>

      <!-- Input -->
      <form class="pcm__input-row" @submit.prevent="send">
        <button
          type="button"
          class="pcm__icon-btn"
          :aria-label="$t('Components.PrivateChatModal.emoji')"
        >
          <i class="far fa-smile"></i>
        </button>
        <input
          v-model="draft"
          type="text"
          class="pcm__input"
          :placeholder="$t('Components.PrivateChatModal.typeMessage')"
          :aria-label="$t('Components.PrivateChatModal.chatMessage')"
          :disabled="sending || !currentChatId"
        />
        <input
          ref="fileInput"
          type="file"
          class="pcm__hidden-file"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
          @change="onFileSelected"
        />
        <button
          type="button"
          class="pcm__icon-btn"
          :aria-label="$t('Components.PrivateChatModal.attachFile')"
          :disabled="!activeRecipient || sending || uploadingFile"
          @click="triggerFilePicker"
        >
          <i v-if="uploadingFile" class="fas fa-circle-notch fa-spin"></i>
          <i v-else class="fas fa-paperclip"></i>
        </button>
        <button
          type="submit"
          class="pcm__send-btn"
          :disabled="
            (!draft.trim() && !pendingAttachment) || sending || !currentChatId
          "
          :aria-label="$t('Components.PrivateChatModal.send')"
        >
          <i class="far fa-paper-plane"></i>
        </button>
      </form>

      <!-- Pending attachment preview (shown above the input until sent) -->
      <div v-if="pendingAttachment" class="pcm__attach-preview">
        <div
          class="pcm__attach-icon"
          :class="'pcm__attach-icon--' + pendingAttachment.kind"
        >
          <i v-if="pendingAttachment.kind === 'image'" class="far fa-image"></i>
          <i v-else class="far fa-file"></i>
        </div>
        <div class="pcm__attach-info">
          <div class="pcm__attach-name">{{ pendingAttachment.name }}</div>
          <div class="pcm__attach-size">
            {{ formatFileSize(pendingAttachment.size) }}
          </div>
        </div>
        <button
          type="button"
          class="pcm__attach-remove"
          @click="clearAttachment"
          :aria-label="$t('Components.PrivateChatModal.removeAttachment')"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import Vue from 'vue';
import Avatar from '@/components/shared/Avatar';
import { getPusherInstance } from '@/utils';
import focusTrapMixin from '@/mixins/focusTrap';

export default {
  name: 'PrivateChatModal',
  components: { Avatar },
  mixins: [focusTrapMixin],
  props: {
    recipient: { type: Object, default: null }
  },
  data() {
    return {
      activeRecipient: this.recipient || null,
      draft: '',
      sending: false,
      loading: true,
      pollTimer: null,
      searchInput: '',
      pendingAttachment: null,
      uploadingFile: false
    };
  },
  computed: {
    ...mapGetters('Authentication', { profile: 'getUser' }),
    ...mapGetters('Chat', [
      'getChats',
      'getChatWithUser',
      'getCurrentChatID',
      'getCurrentChatMessages'
    ]),
    onlineUserIds() {
      return (
        (this.$store.state.Users && this.$store.state.Users.onlineUserIds) || []
      );
    },
    allUsers() {
      const items =
        (this.$store.state.Users && this.$store.state.Users.items) || [];
      if (Array.isArray(items)) return items;
      return Object.values(items);
    },
    recentChats() {
      // Chats sorted by the most recent message
      const chats = (this.getChats || []).slice();
      return chats.sort((a, b) => {
        const ta = this.lastMessageAt(a);
        const tb = this.lastMessageAt(b);
        return new Date(tb) - new Date(ta);
      });
    },
    filteredRecent() {
      const q = (this.searchInput || '').trim().toLowerCase();
      if (!q) return this.recentChats;
      return this.recentChats.filter((chat) => {
        const u = this.getChatCounterpart(chat);
        const name = `${u.Firstname || ''} ${u.Lastname || ''}`.toLowerCase();
        return name.includes(q);
      });
    },
    // Attendees that don't already have a chat thread (to avoid double-listing)
    otherAttendees() {
      const ownId = this.profile && this.profile.UserID;
      const existingChatUserIds = new Set(
        (this.recentChats || []).map((c) => {
          const u = this.getChatCounterpart(c);
          return u && u.UserID;
        })
      );
      return this.allUsers.filter((u) => {
        if (!u || !u.UserID) return false;
        if (u.UserID === ownId) return false;
        if (existingChatUserIds.has(u.UserID)) return false;
        return true;
      });
    },
    filteredOtherAttendees() {
      const q = (this.searchInput || '').trim().toLowerCase();
      let list = this.otherAttendees;
      if (q) {
        list = list.filter((u) => {
          const name = `${u.Firstname || ''} ${u.Lastname || ''}`.toLowerCase();
          const company = (u.Company || '').toLowerCase();
          const job = (u.Jobtitle || '').toLowerCase();
          return name.includes(q) || company.includes(q) || job.includes(q);
        });
      }
      // Sort online first, then alphabetical
      return list.slice().sort((a, b) => {
        const aOn = this.isUserOnline(a.UserID) ? 0 : 1;
        const bOn = this.isUserOnline(b.UserID) ? 0 : 1;
        if (aOn !== bOn) return aOn - bOn;
        return `${a.Firstname || ''}`.localeCompare(`${b.Firstname || ''}`);
      });
    },
    currentChat() {
      return this.activeRecipient && this.getChatWithUser
        ? this.getChatWithUser(this.activeRecipient.UserID)
        : null;
    },
    currentChatId() {
      return (this.currentChat && this.currentChat.ChatID) || null;
    },
    messages() {
      if (!this.currentChatId) return [];
      const chat = this.getChats.find((c) => c.ChatID === this.currentChatId);
      const list = (chat && chat.Messages) || [];
      return list
        .slice()
        .sort((a, b) => new Date(a.CreatedAt) - new Date(b.CreatedAt));
    }
  },
  watch: {
    activeRecipient(newRecipient) {
      if (!newRecipient) return;
      this.loading = true;
      this.resolveChat(newRecipient).finally(() => {
        this.loading = false;
        this.scrollToBottom();
      });
    }
  },
  async created() {
    document.body.classList.add('overflow-hidden');
    try {
      await this.loadChatData();
    } catch (e) {
      /* ignore */
    }
    // Also load the attendees list so the left panel has names
    try {
      await this.$store.dispatch('Users/fetchUsers', {
        page: 1,
        pageSize: 100,
        search: '',
        groupId: ''
      });
    } catch (e) {
      /* ignore */
    }

    if (this.activeRecipient && this.activeRecipient.UserID) {
      await this.resolveChat(this.activeRecipient);
    }

    this.loading = false;
    this.scrollToBottom();
    this.activateFocusTrap();

    try {
      getPusherInstance().bind('chat-new-message', this.onIncomingMessage);
    } catch (e) {
      /* ignore */
    }

    this.pollTimer = setInterval(() => {
      this.loadChatData().catch(() => {});
    }, 5000);
  },
  beforeDestroy() {
    this.deactivateFocusTrap();
    document.body.classList.remove('overflow-hidden');
    if (this.pollTimer) clearInterval(this.pollTimer);
    try {
      getPusherInstance().unbind('chat-new-message', this.onIncomingMessage);
    } catch (e) {
      /* ignore */
    }
  },
  methods: {
    ...mapActions('Chat', [
      'loadChatData',
      'createNewChat',
      'sendChatMessage',
      'activateChat'
    ]),
    ...mapMutations('Chat', ['addMessage']),

    async resolveChat(user) {
      if (!user || !user.UserID) return;
      let chat = this.getChatWithUser(user.UserID);
      if (!chat) {
        try {
          const response = await this.createNewChat(user.UserID);
          chat = response && response.data;
          await this.loadChatData();
          chat = this.getChatWithUser(user.UserID) || chat;
        } catch (e) {
          /* ignore */
        }
      }
      if (chat && chat.ChatID) this.activateChat(chat);
    },

    switchToUser(user) {
      this.activeRecipient = user;
      this.draft = '';
    },
    switchToChat(chat) {
      const user = this.getChatCounterpart(chat);
      this.switchToUser(user);
    },

    isUserOnline(userId) {
      if (!userId) return false;
      if (this.onlineUserIds.includes(userId)) return true;
      const user = this.allUsers.find((u) => u && u.UserID === userId);
      if (user) {
        const flag = user.Online;
        return flag === true || flag === 1 || flag === '1';
      }
      return false;
    },
    getChatCounterpart(chat) {
      if (!chat || !chat.Users) return {};
      const ownId = this.profile && this.profile.UserID;
      return (
        chat.Users.find((u) => u && u.UserID !== ownId) || chat.Users[0] || {}
      );
    },
    userFullName(user) {
      if (!user) return '';
      const first = user.Firstname || user.firstName || user.FirstName || '';
      const last = user.Lastname || user.lastName || user.LastName || '';
      return `${first} ${last}`.trim();
    },
    lastMessageAt(chat) {
      if (!chat || !chat.Messages || !chat.Messages.length) return null;
      const last = chat.Messages[chat.Messages.length - 1];
      return last && (last.CreatedAt || last.createdAt);
    },
    lastMessagePreview(chat) {
      if (!chat || !chat.Messages || !chat.Messages.length)
        return this.$t('Components.PrivateChatModal.previewNoMessages');
      const last = chat.Messages[chat.Messages.length - 1];
      const ownId = this.profile && this.profile.UserID;
      const text = (last.Message || '').trim();
      const preview = text.length > 40 ? text.slice(0, 37) + '…' : text;
      if (last.UserID === ownId) {
        return this.$t('Components.PrivateChatModal.youPrefix', {
          message: preview || '...'
        });
      }
      return preview || '...';
    },
    isOwnMessage(msg) {
      return msg && msg.UserID === (this.profile && this.profile.UserID);
    },
    formatTime(date) {
      if (!date) return '';
      return Vue.moment(date).format('h:mm A');
    },
    formatChatTime(date) {
      if (!date) return '';
      const now = Vue.moment();
      const m = Vue.moment(date);
      if (m.isSame(now, 'day')) return m.format('HH:mm');
      if (m.isSame(now, 'week')) return m.format('ddd');
      return m.format('DD-MM');
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const list = this.$refs.messageList;
        if (list) list.scrollTop = list.scrollHeight;
      });
    },
    onIncomingMessage() {
      this.loadChatData()
        .then(() => this.scrollToBottom())
        .catch(() => {});
    },
    async send() {
      const text = this.draft.trim();
      const hasAttach = !!this.pendingAttachment;
      if ((!text && !hasAttach) || this.sending || !this.currentChatId) return;
      this.sending = true;
      try {
        // Build the outgoing message. If an attachment is staged, embed
        // it inline as a base64 data URL so the recipient sees it
        // rendered directly (image) or as a downloadable file chip.
        let message = text;
        if (hasAttach) {
          const a = this.pendingAttachment;
          if (a.kind === 'image') {
            // Inline image bubble — recipient sees it rendered as <img>
            const caption = text ? text + '<br>' : '';
            message = `${caption}<img src="${a.dataUrl}" alt="${a.name}" class="pcm-msg-img" />`;
          } else {
            // Non-image file — send as an anchor the recipient can save
            const caption = text ? text + '<br>' : '';
            const sizeStr = this.formatFileSize(a.size);
            message = `${caption}<a href="${a.dataUrl}" download="${a.name}" class="pcm-msg-file"><span class="pcm-msg-file-name">📎 ${a.name}</span><span class="pcm-msg-file-size">${sizeStr}</span></a>`;
          }
        }
        await this.sendChatMessage({ chatid: this.currentChatId, message });
        this.draft = '';
        this.pendingAttachment = null;
        await this.loadChatData();
        this.scrollToBottom();
      } catch (e) {
        /* ignore */
      } finally {
        this.sending = false;
      }
    },

    // ── File attachments ────────────────────────────────────────
    triggerFilePicker() {
      if (!this.activeRecipient) return;
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    onFileSelected(e) {
      const file = e.target.files && e.target.files[0];
      // Reset the input so the same file can be picked again later
      if (e.target) e.target.value = '';
      if (!file) return;
      // Cap at ~1 MB because we embed as base64 in the message body
      const MAX = 1024 * 1024;
      if (file.size > MAX) {
        alert(this.$t('Components.PrivateChatModal.fileTooLarge'));
        return;
      }
      this.uploadingFile = true;
      const reader = new FileReader();
      reader.onload = () => {
        this.pendingAttachment = {
          name: file.name,
          size: file.size,
          type: file.type,
          kind: file.type.startsWith('image/') ? 'image' : 'file',
          dataUrl: reader.result
        };
        this.uploadingFile = false;
      };
      reader.onerror = () => {
        this.uploadingFile = false;
        alert(this.$t('Components.PrivateChatModal.fileReadFailed'));
      };
      reader.readAsDataURL(file);
    },
    clearAttachment() {
      this.pendingAttachment = null;
    },
    formatFileSize(bytes) {
      if (!bytes || bytes <= 0) return '0 B';
      const units = ['B', 'KB', 'MB', 'GB'];
      let n = bytes;
      let u = 0;
      while (n >= 1024 && u < units.length - 1) {
        n /= 1024;
        u++;
      }
      return `${n.toFixed(n < 10 && u > 0 ? 1 : 0)} ${units[u]}`;
    },
    renderMessage(raw) {
      if (!raw) return '';
      // The backend stores messages as plain text. Our own sent messages
      // may include safe inline HTML (<img> for image attachments and
      // <a class="pcm-msg-file"> for downloadable file chips). Strip
      // everything except those two patterns so we don't enable XSS.
      const allowedImg =
        /<img\s+src="(data:image\/[a-z+]+;base64,[a-zA-Z0-9+/=]+)"(?:\s+alt="([^"]*)")?(?:\s+class="pcm-msg-img")?\s*\/?>/gi;
      const allowedFile =
        /<a\s+href="(data:[^"]+)"\s+download="([^"]+)"\s+class="pcm-msg-file">\s*<span class="pcm-msg-file-name">([^<]+)<\/span>\s*<span class="pcm-msg-file-size">([^<]+)<\/span>\s*<\/a>/gi;
      const br = /<br\s*\/?>(\s*)/gi;

      // Pull out the safe tokens, escape everything else, then re-insert.
      const tokens = [];
      let working = raw;
      working = working.replace(allowedImg, (match, src, alt) => {
        const idx = tokens.length;
        tokens.push(
          `<img src="${src}" alt="${this.escapeHtml(
            alt || 'image'
          )}" class="pcm-msg-img" />`
        );
        return `@@TOKEN${idx}@@`;
      });
      working = working.replace(allowedFile, (match, href, dl, name, size) => {
        const idx = tokens.length;
        tokens.push(
          `<a href="${href}" download="${this.escapeHtml(
            dl
          )}" class="pcm-msg-file">` +
            `<span class="pcm-msg-file-name">${this.escapeHtml(name)}</span>` +
            `<span class="pcm-msg-file-size">${this.escapeHtml(size)}</span>` +
            `</a>`
        );
        return `@@TOKEN${idx}@@`;
      });
      working = working.replace(br, () => {
        const idx = tokens.length;
        tokens.push('<br>');
        return `@@TOKEN${idx}@@`;
      });
      // Escape all remaining HTML
      working = this.escapeHtml(working);
      // Re-insert allowed tokens
      working = working.replace(
        /@@TOKEN(\d+)@@/g,
        (_, i) => tokens[parseInt(i, 10)] || ''
      );
      return working;
    },
    escapeHtml(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    onClose() {
      document.body.classList.remove('overflow-hidden');
      this.$emit('close');
    }
  }
};
</script>

<style scoped lang="scss">
.pcm {
  display: flex;
  height: 100%;
  background: #ffffff;
  border-radius: 16px;
  font-family: 'Poppins', sans-serif;
  overflow: hidden;

  @media (max-width: 640px) {
    border-radius: 0;
  }
}

// ══ LEFT SIDE PANEL ════════════════════════════════════════════════
.pcm__side {
  width: 300px;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
  }
}

.pcm__side-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 12px;
  flex-shrink: 0;
}

.pcm__side-title {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.pcm__search-wrap {
  position: relative;
  padding: 0 16px 12px;
  flex-shrink: 0;
}

.pcm__search-icon {
  position: absolute;
  top: 50%;
  left: 28px;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 12px;
}

.pcm__search-input {
  width: 100%;
  padding: 9px 12px 9px 34px;
  background: #f3f4f6;
  border: 1.5px solid #b0b8c4;
  border-radius: 10px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #111827;

  &::placeholder {
    color: #6b7280;
  }
  &:focus {
    outline: none;
    background: #ffffff;
    border-color: var(--color-primary, #047873);
  }
}

.pcm__section {
  padding: 0 8px;
  margin-bottom: 12px;
  overflow-y: auto;

  &:last-child {
    flex: 1;
    min-height: 0;
  }
}

.pcm__section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px 4px;
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  text-transform: none;

  i {
    font-size: 10px;
    color: #6b7280;
  }
}

.pcm__section-count {
  margin-left: auto;
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}

.pcm__conv {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: none;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  font-family: 'Poppins', sans-serif;
  transition: background 0.12s;
  position: relative;

  &:hover {
    background: #f3f4f6;
  }

  &--active {
    background: #e6f5f4;

    &:hover {
      background: #d6efed;
    }

    .pcm__conv-name {
      color: var(--color-primary, #047873);
    }
  }

  &--compact {
    padding: 8px 12px;
  }
}

.pcm__conv-avatar-wrap {
  position: relative;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.pcm__conv-avatar {
  width: 40px !important;
  height: 40px !important;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;

  ::v-deep img,
  ::v-deep .avatar {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    border-radius: 50%;
  }
}

.pcm__conv-dot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #22c55e;
  border: 2px solid #ffffff;
}

.pcm__conv-body {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.pcm__conv-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}

.pcm__conv-name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.pcm__conv-time {
  font-size: 10px;
  color: #6b7280;
  font-weight: 500;
  flex-shrink: 0;
}

.pcm__conv-preview {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pcm__conv-role {
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
}

.pcm__empty-side {
  text-align: center;
  padding: 28px 14px;
  color: #6b7280;

  i {
    font-size: 24px;
    color: #d1d5db;
    display: block;
    margin-bottom: 8px;
  }
  p {
    margin: 0;
    font-size: 12px;
  }
}

// ══ RIGHT MAIN PANEL ══════════════════════════════════════════════
.pcm__main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #ffffff;

  @media (max-width: 768px) {
    // On mobile, hide the main panel when no conversation is selected
    // so the side list is full-width
  }
}

// ── Chat header ────────────────────────────────────────────────────
.pcm__chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  flex-shrink: 0;
}

.pcm__user {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;

  &--placeholder {
    opacity: 0.6;
  }
}

.pcm__side-back {
  display: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f3f4f6;
  color: #374151;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  font-size: 12px;

  &:hover {
    background: #e5e7eb;
  }

  @media (max-width: 768px) {
    display: flex;
  }
}

.pcm__avatar-wrap {
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;

  &--placeholder {
    background: #f3f4f6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    font-size: 18px;
  }
}

.pcm__avatar {
  width: 44px !important;
  height: 44px !important;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;

  ::v-deep img,
  ::v-deep .avatar {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    border-radius: 50%;
  }
}

.pcm__avatar-dot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #22c55e;
  border: 2px solid #ffffff;
}

.pcm__user-info {
  min-width: 0;
  flex: 1;
}

.pcm__name {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pcm__role {
  font-size: 12px;
  color: #6b7280;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pcm__close {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: #f3f4f6;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: background 0.15s;
  flex-shrink: 0;

  &:hover {
    background: #e5e7eb;
  }

  &--mobile-only {
    display: none;

    @media (max-width: 768px) {
      display: flex;
    }
  }
}

// ── Messages area ─────────────────────────────────────────────────
.pcm__messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pcm__msg-wrap {
  display: flex;
  width: 100%;
}

.pcm__msg {
  display: flex;
  gap: 8px;
  max-width: 75%;

  &--own {
    margin-left: auto;
    flex-direction: row-reverse;

    .pcm__msg-col {
      align-items: flex-end;
    }
    .pcm__bubble {
      background: var(--color-primary, #047873);
      color: #ffffff;
      border-bottom-right-radius: 4px;
      border-bottom-left-radius: 12px;

      // Own bubble: flip file-chip colors so they remain legible
      ::v-deep a.pcm-msg-file {
        background: rgba(255, 255, 255, 0.14);
        border-color: rgba(255, 255, 255, 0.3);
        color: #ffffff;

        &:hover {
          background: rgba(255, 255, 255, 0.22);
          border-color: rgba(255, 255, 255, 0.55);
        }

        .pcm-msg-file-size {
          color: rgba(255, 255, 255, 0.75);
        }
      }
    }
    .pcm__msg-time {
      text-align: right;
    }
  }
}

.pcm__msg-avatar {
  width: 32px !important;
  height: 32px !important;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  align-self: flex-end;

  ::v-deep img,
  ::v-deep .avatar {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    border-radius: 50%;
  }
}

.pcm__msg-col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.pcm__msg-sender {
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
  padding-left: 4px;
}

.pcm__bubble {
  background: #ffffff;
  color: #111827;
  padding: 10px 14px;
  border-radius: 12px;
  border-bottom-left-radius: 4px;
  font-size: 14px;
  line-height: 1.45;
  word-wrap: break-word;
  border: 1px solid #e5e7eb;
  max-width: 100%;

  // Inline image attachments (v-html rendered)
  ::v-deep img.pcm-msg-img {
    display: block;
    max-width: 280px;
    max-height: 280px;
    width: auto;
    height: auto;
    margin: 4px 0 2px;
    border-radius: 8px;
    cursor: zoom-in;
  }

  // Non-image file chip (v-html rendered)
  ::v-deep a.pcm-msg-file {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    margin: 4px 0 2px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    text-decoration: none;
    color: #111827;
    transition: background 0.12s, border-color 0.12s;
    max-width: 240px;

    &:hover {
      background: #ffffff;
      border-color: var(--color-primary, #047873);
    }

    .pcm-msg-file-name {
      font-size: 13px;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
      min-width: 0;
    }

    .pcm-msg-file-size {
      font-size: 11px;
      color: #6b7280;
      flex-shrink: 0;
    }
  }
}

.pcm__msg-time {
  font-size: 10px;
  color: #6b7280;
  padding: 0 4px;
}

// ── Empty states ──────────────────────────────────────────────────
.pcm__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  flex: 1;
  color: #6b7280;

  &--in-chat {
    padding: 40px 20px;
  }
}

.pcm__empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #e6f5f4;
  color: var(--color-primary, #047873);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.pcm__empty-text {
  font-size: 14px;
  text-align: center;
  line-height: 1.5;
  margin: 0;
}

.pcm__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

// ── Input row ─────────────────────────────────────────────────────
.pcm__input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  flex-shrink: 0;
}

.pcm__icon-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  transition: background 0.12s, color 0.12s;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: var(--color-primary, #047873);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.pcm__input {
  flex: 1 1 0;
  // Without min-width: 0 an <input> keeps its intrinsic min width and
  // refuses to shrink, so on narrow phones the row overflows and the
  // trailing send button gets pushed off-screen. Let it shrink.
  min-width: 0;
  padding: 11px 16px;
  background: #f3f4f6;
  border: 1.5px solid #b0b8c4;
  border-radius: 12px;
  font-size: 14px;
  outline: none;
  font-family: 'Poppins', sans-serif;

  &::placeholder {
    color: #6b7280;
  }
  &:focus {
    border-color: var(--color-primary, #047873);
    background: #ffffff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.pcm__hidden-file {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

// ── Pending attachment preview (before send) ─────────────────────
.pcm__attach-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 16px 10px;
  padding: 10px 12px;
  background: #e6f5f4;
  border: 1.5px solid var(--color-primary, #047873);
  border-radius: 10px;
}

.pcm__attach-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;

  &--image {
    background: #dbeafe;
    color: #1d4ed8;
  }
  &--file {
    background: #fef3c7;
    color: #d97706;
  }
}

.pcm__attach-info {
  flex: 1;
  min-width: 0;
}

.pcm__attach-name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pcm__attach-size {
  font-size: 11px;
  color: #6b7280;
  margin-top: 1px;
}

.pcm__attach-remove {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: #ffffff;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
  transition: background 0.12s, color 0.12s;

  &:hover {
    background: #fee2e2;
    color: #dc2626;
  }
}

.pcm__send-btn {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: none;
  background: var(--color-primary, #047873);
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: var(--color-primary-hover, #036663);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

// ── Mobile responsive: hide side when chat open ───────────────────
@media (max-width: 768px) {
  .pcm__side {
    display: flex;
  }
  .pcm__main {
    display: none;
  }
  .pcm.pcm--chat-open .pcm__side {
    display: none;
  }
  .pcm.pcm--chat-open .pcm__main {
    display: flex;
  }
}
</style>
